import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import { API_BASE_URL } from './config/api';

function isLoopbackHost(hostname: string): boolean {
    const value = String(hostname || '').trim().toLowerCase();
    return value === 'localhost' || value === '127.0.0.1' || value === '::1';
}

const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
const originalFetch = window.fetch.bind(window);
const API_ROUTE_PREFIXES = [
    '/customers',
    '/legacy',
    '/estimates',
    '/inquiries',
    '/settings',
    '/system',
    '/auth',
    '/copilot',
    '/sales',
    '/delivery',
    '/purchasing',
    '/inventory',
    '/financials',
    '/manufacturing',
    '/master-data',
    '/materials',
    '/reports',
    '/overhead',
    '/design-mix',
    '/production-logs',
    '/integration',
    '/workflow',
    '/accounting',
];

function normalizeRequestUrl(rawUrl: string): string {
    try {
        const parsed = new URL(rawUrl, window.location.origin);
        const currentHost = window.location.hostname;
        const sameHost = parsed.hostname === currentHost
            || (isLoopbackHost(parsed.hostname) && isLoopbackHost(currentHost));
        if (!sameHost) return parsed.toString();

        if (window.location.protocol === 'https:' && parsed.protocol === 'http:') {
            parsed.protocol = 'https:';
        }

        const path = parsed.pathname || '/';
        if (path.startsWith('/proxy/')) {
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
        const hasApiPrefix = path === '/api' || path.startsWith('/api/');
        const needsApiPrefix = API_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

        if (!hasApiPrefix && needsApiPrefix) {
            if (API_BASE_URL.startsWith('/proxy/')) {
                parsed.pathname = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
            } else {
                parsed.pathname = `/api${path.startsWith('/') ? path : `/${path}`}`;
            }
        }

        if (parsed.origin === window.location.origin) {
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
        return parsed.toString();
    } catch {
        return rawUrl;
    }
}

window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string'
        ? input
        : input instanceof Request
            ? input.url
            : input.toString();
    const normalizedUrl = normalizeRequestUrl(url);
    const shouldIncludeCredentials = normalizedUrl.startsWith('/') || (() => {
        try {
            return new URL(normalizedUrl, window.location.origin).origin === apiOrigin;
        } catch {
            return false;
        }
    })();
    const nextInit = { ...(init || {}) };
    if (shouldIncludeCredentials && nextInit.credentials === undefined) {
        nextInit.credentials = 'include';
    }
    const requestInput = input instanceof Request
        ? (normalizedUrl === input.url ? input : new Request(normalizedUrl, input))
        : normalizedUrl;
    return originalFetch(requestInput, nextInit).then((res) => {
        if (res.status === 401) {
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return res;
    });
};

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <App />
    </React.StrictMode>,
)
